import { useEffect, useState } from 'react';
import { http } from '@/lib/api';
import { toast } from 'sonner';
import { Plus, Pin, Trash2, Search, MessageCircleHeart, Pencil, Check, X, History } from 'lucide-react';

function formatWhen(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff/60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff/3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff/86400)}d ago`;
    return d.toLocaleDateString();
  } catch { return ''; }
}

export default function CoachThreads({ open, onClose, currentSessionId, onSelect, onNewChat }) {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await http.get('/coach/sessions');
      setSessions(r.data || []);
    } catch { toast.error("Couldn't load conversations"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); }, [open]);

  const filtered = sessions.filter(s => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (s.title || '').toLowerCase().includes(q) || (s.preview || '').toLowerCase().includes(q);
  });

  const pin = async (session_id, current) => {
    try {
      const r = await http.patch(`/coach/sessions/${session_id}`, { pinned: !current });
      setSessions(prev => prev.map(s => s.session_id === session_id ? { ...s, pinned: r.data.pinned } : s)
        .sort((a, b) => (a.pinned === b.pinned ? 0 : a.pinned ? -1 : 1)));
    } catch { toast.error("Couldn't pin"); }
  };

  const startEdit = (s) => { setEditingId(s.session_id); setEditValue(s.title || ''); };
  const cancelEdit = () => { setEditingId(null); setEditValue(''); };
  const saveEdit = async (session_id) => {
    try {
      const r = await http.patch(`/coach/sessions/${session_id}`, { title: editValue });
      setSessions(prev => prev.map(s => s.session_id === session_id ? { ...s, title: r.data.title || s.title } : s));
      cancelEdit();
      toast.success('Renamed');
    } catch { toast.error("Couldn't rename"); }
  };

  const remove = async (session_id) => {
    if (!window.confirm('Delete this conversation? This cannot be undone.')) return;
    try {
      await http.delete(`/coach/sessions/${session_id}`);
      setSessions(prev => prev.filter(s => s.session_id !== session_id));
      if (currentSessionId === session_id && onNewChat) onNewChat();
      toast.success('Conversation deleted');
    } catch { toast.error("Couldn't delete"); }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex justify-end animate-fade-in"
      style={{ backgroundColor: 'rgba(11,25,44,0.6)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)' }}
      onClick={onClose}
      data-testid="coach-threads-overlay"
    >
      <aside
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md h-full bg-white flex flex-col animate-slide-in-right shadow-2xl"
        data-testid="coach-threads-panel"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-navy/5">
          <div className="flex items-center gap-2">
            <History size={16} className="text-navy/50" strokeWidth={1.75}/>
            <p className="font-heading text-lg text-navy">Your conversations</p>
          </div>
          <button
            onClick={onClose}
            data-testid="coach-threads-close"
            aria-label="Close"
            className="p-1.5 rounded-full hover:bg-beige transition-smooth"
          >
            <X size={16} className="text-navy/60"/>
          </button>
        </div>

        <div className="px-5 py-3 space-y-3 border-b border-navy/5">
          <button
            onClick={() => { if (onNewChat) onNewChat(); onClose(); }}
            data-testid="coach-threads-new-chat"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-navy text-white text-sm font-medium hover:bg-navy-soft transition-smooth"
          >
            <Plus size={14} strokeWidth={2}/> New conversation
          </button>
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy/40"/>
            <input
              data-testid="coach-threads-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search your chats"
              className="w-full pl-9 pr-3 py-2 rounded-full text-sm bg-beige/70 border border-navy/5 outline-none focus:border-navy/20"
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-3 py-3">
          {loading && <p className="text-xs text-navy/50 px-3">Loading…</p>}
          {!loading && filtered.length === 0 && (
            <div className="text-center py-12 px-6" data-testid="coach-threads-empty">
              <MessageCircleHeart size={26} className="mx-auto text-navy/30" strokeWidth={1.5}/>
              <p className="font-heading text-lg text-navy mt-3">No conversations yet.</p>
              <p className="text-xs text-navy/55 mt-1">Start a new one from the button above.</p>
            </div>
          )}
          {filtered.map((s) => {
            const isActive = s.session_id === currentSessionId;
            const isEditing = editingId === s.session_id;
            return (
              <div
                key={s.session_id}
                data-testid={`coach-thread-${s.session_id}`}
                className={`group px-3 py-2.5 rounded-2xl transition-smooth ${isActive ? 'bg-beige' : 'hover:bg-beige/50'}`}
              >
                <div className="flex items-start gap-2">
                  <div className="flex-1 min-w-0 cursor-pointer" onClick={() => { if (!isEditing) { onSelect(s.session_id); onClose(); } }}>
                    {isEditing ? (
                      <input
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') saveEdit(s.session_id); if (e.key === 'Escape') cancelEdit(); }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-full text-sm font-heading text-navy bg-white border border-navy/20 rounded-lg px-2 py-1 outline-none"
                        data-testid={`coach-thread-title-input-${s.session_id}`}
                      />
                    ) : (
                      <p className="text-sm font-heading text-navy truncate flex items-center gap-1.5">
                        {s.pinned && <Pin size={11} className="text-gold shrink-0" fill="currentColor"/>}
                        {s.title || 'New chat'}
                      </p>
                    )}
                    <p className="text-[11px] text-navy/50 mt-0.5 truncate">{s.preview}</p>
                    <p className="text-[10px] uppercase tracking-widest text-navy/40 mt-1">
                      {formatWhen(s.at)} · {s.message_count || 0} msgs
                    </p>
                  </div>
                  <div className="flex items-center opacity-0 group-hover:opacity-100 transition-smooth shrink-0">
                    {isEditing ? (
                      <>
                        <button onClick={() => saveEdit(s.session_id)} data-testid={`coach-thread-save-${s.session_id}`} className="p-1.5 rounded-full hover:bg-white" aria-label="Save"><Check size={13} className="text-green-700"/></button>
                        <button onClick={cancelEdit} className="p-1.5 rounded-full hover:bg-white" aria-label="Cancel"><X size={13} className="text-navy/60"/></button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => pin(s.session_id, s.pinned)} data-testid={`coach-thread-pin-${s.session_id}`} className="p-1.5 rounded-full hover:bg-white" aria-label="Pin">
                          <Pin size={13} className={s.pinned ? 'text-gold' : 'text-navy/50'} fill={s.pinned ? 'currentColor' : 'none'}/>
                        </button>
                        <button onClick={() => startEdit(s)} data-testid={`coach-thread-rename-${s.session_id}`} className="p-1.5 rounded-full hover:bg-white" aria-label="Rename"><Pencil size={13} className="text-navy/50"/></button>
                        <button onClick={() => remove(s.session_id)} data-testid={`coach-thread-delete-${s.session_id}`} className="p-1.5 rounded-full hover:bg-white" aria-label="Delete"><Trash2 size={13} className="text-red-500/80"/></button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </aside>
    </div>
  );
}
